import { useState, useEffect, useContext } from "react";
import "bootstrap/dist/css/bootstrap.min.css";
import {
  FaGraduationCap,
  FaVenusMars,
  FaBuilding,
  FaIdBadge,
  FaUserShield,
  FaHistory,
  FaExclamationTriangle,
} from "react-icons/fa";
import {
  ArrowLeft,
  ShieldOff,
  ShieldCheck,
  Ban,
  CheckCircle,
  // MoreVertical
} from "lucide-react";
import { useNavigate, useParams } from "react-router-dom";
import {
  blockUserAPI,
  demoteUserAPI,
  promoteUserAPI,
  unblockUserAPI,
  viewUserProfileAPI,
} from "../../services/userService";
import { Button, Modal, Spinner, Badge } from "react-bootstrap";
import { userRoles } from "../../App";
import { AuthContext } from "../../context/AuthContext";

export default function ViewUserProfile() {
  const navigate = useNavigate();
  const { userId } = useParams();
  const { user } = useContext(AuthContext);

  const [userP, setUser] = useState(null);

  const [loading, setLoading] = useState(true);
  const [errorMsg, setErrorMsg] = useState("");

  // Modal State
  const [showModal, setShowModal] = useState(false);
  const [actionType, setActionType] = useState(null); // 'block', 'unblock', 'promote', 'demote'
  const [actionLoading, setActionLoading] = useState(false);
  const [isOnline, setIsOnline] = useState(false);

  // -------- FETCH REAL USER DATA --------
  useEffect(() => {
    async function getUserProfile() {
      try {
        setLoading(true);
        setErrorMsg("");
        const response = await viewUserProfileAPI(`${userId}`);
        setIsOnline(response.isOnline);
        
        if (!response || !response.user) {
          setErrorMsg("User data not found.");
          return;
        }
        setUser(response.user);
      } catch (error) {
        console.error(error);
        setErrorMsg(error.message || "Failed to load user profile.");
      } finally {
        setLoading(false);
      }
    }
    getUserProfile();
  }, [userId]);

  // ----------- Handlers ----------
  function showScoreHistory() {
    navigate("/user/profile/showPointsHistory", { state: { userId } });
  }

  // 1. Trigger the Modal
  const confirmActionTrigger = (type) => {
    setActionType(type);
    setShowModal(true);
  };

  // 2. Execute Action (API Call)
  const handleConfirmAction = async () => {
    if (!userP || !userP._id) return;

    setActionLoading(true);
    try {
      switch (actionType) {
        case "make-supervisor":
          await promoteUserAPI(userP._id);
          // تحديث الحالة محلياً عشان الـ UI يتغير فوراً
          setUser((prev) => ({ ...prev, role: userRoles.supervisor }));
          break;

        case "remove-supervisor":
          await demoteUserAPI(userP._id);
          setUser((prev) => ({ ...prev, role: userRoles.student }));
          break;

        case "block-user":
          await blockUserAPI(userP._id);
          setUser((prev) => ({ ...prev, status: "BLOCKED" }));
          break;

        case "unblock-user":
          await unblockUserAPI(userP._id);
          setUser((prev) => ({ ...prev, status: "ACTIVE" }));
          break;

        default:
          break;
      }

      // لو العملية نجحت، نقفل المودال
      setShowModal(false);

      // (اختياري) ممكن تظهر رسالة نجاح هنا لو عندك نظام Toast
      // toast.success("Action completed successfully");
    } catch (error) {
      console.error("Action error:", error);
      // (اختياري) أظهر رسالة خطأ
      // setErrorMsg(error.message);
    } finally {
      setActionLoading(false);
    }
  };

  // ----------- Render Helpers ----------
  const getModalContent = () => {
    switch (actionType) {
      case "block-user":
        return {
          title: "Block User",
          body: `Are you sure you want to block ${userP?.fullName}? They will no longer be able to access the system.`,
          variant: "danger",
          btnText: "Yes, Block User",
          icon: <Ban size={30} className="text-danger mb-3" />,
        };
      case "unblock-user":
        return {
          title: "Unblock User",
          body: `Are you sure you want to unblock ${userP?.fullName}? Access will be restored.`,
          variant: "success",
          btnText: "Yes, Unblock",
          icon: <CheckCircle size={30} className="text-success mb-3" />,
        };
      case "make-supervisor":
        return {
          title: "Promote to Supervisor",
          body: `This will grant ${userP?.fullName} supervisor privileges. Are you sure?`,
          variant: "primary",
          btnText: "Confirm Promotion",
          icon: <ShieldCheck size={30} className="text-primary mb-3" />,
        };
      case "remove-supervisor":
        return {
          title: "Remove Supervisor Role",
          body: `This will downgrade ${userP?.fullName} to a regular student. Continue?`,
          variant: "warning",
          btnText: "Confirm Downgrade",
          icon: <ShieldOff size={30} className="text-warning mb-3" />,
        };
      default:
        return { title: "", body: "", variant: "primary", btnText: "Confirm" };
    }
  };

  const modalContent = getModalContent();

  // ----------- Loading State ----------
  if (loading) {
    return (
      <div
        className="d-flex flex-column justify-content-center align-items-center vh-100"
        style={{ background: "#f8f9fa" }}
      >
        <Spinner
          animation="border"
          variant="primary"
          style={{ width: "3rem", height: "3rem" }}
        />
        <p className="mt-3 text-muted fw-bold">Loading Profile...</p>
      </div>
    );
  }

  // ----------- Error State ----------
  if (errorMsg || !userP) {
    return (
      <div className="d-flex flex-column justify-content-center align-items-center vh-100">
        <FaExclamationTriangle
          size={50}
          className="text-secondary mb-3 opacity-50"
        />
        <h5 className="text-danger mb-3">{errorMsg || "User not found"}</h5>
        <Button
          variant="outline-dark"
          onClick={() => navigate(-1)}
          className="rounded-pill px-4"
        >
          <ArrowLeft size={16} className="me-2" /> Return Back
        </Button>
      </div>
    );
  }

  const userInfo = [
    { icon: <FaIdBadge />, label: "University ID", value: userP?.universtyID },
    { icon: <FaUserShield />, label: "Role", value: userP?.role },
    { icon: <FaGraduationCap />, label: "Level", value: userP?.level },
    { icon: <FaVenusMars />, label: "Gender", value: userP?.gender },
    { icon: <FaBuilding />, label: "Faculty", value: userP?.faculty },
  ];

  return (
    <div className="profile-wrapper">
      <div className="container py-4 py-md-5">
        <div className="card profile-card border-0 overflow-hidden shadow-lg">
          <div className="row g-0 h-100">
            {/* --- LEFT COLUMN (Gradient) --- */}
            <div className="col-lg-4 col-md-5 left-panel d-flex flex-column align-items-center justify-content-center text-center p-4 p-md-5 text-white position-relative">
              {/* Back Button (Mobile Overlay) */}
              <button
                onClick={() => navigate(-1)}
                className="btn btn-link text-white position-absolute top-0 start-0 m-3 text-decoration-none d-md-none"
              >
                <ArrowLeft size={24} />
              </button>

              <div className="avatar-wrapper mb-3">
                <img
                  src={
                    userP?.profileImage?.secure_url
                      ? userP?.profileImage.secure_url
                      : userP?.gender === "MALE"
                        ? "/avatars/MALE_avatar.png"
                        : "/avatars/FEMALE_avatar.png"
                  }
                  alt="User"
                  className="profile-avatar"
                />
                <div
                  className={`status-dot ${userP?.status === "ACTIVE" ? "online" : "offline"}`}
                ></div>
              </div>

              <h2 className="fw-bold mb-1">{userP?.fullName}</h2>
              <p className="opacity-75 mb-3 small text-break">{userP?.email}</p>

              <Badge
                bg="light"
                text="dark"
                className="px-5 py-1 rounded-pill fw-bold text-uppercase mt-3 mx-3 shadow-sm"
              >
                {userP?.role?.toLowerCase()}
              </Badge>

              {/* Action Buttons (Left Panel for Desktop, Bottom for Mobile) */}
              <div className="d-grid gap-2 w-100 mt-auto d-none d-md-block">
                {/* Secondary Action */}
                <Button
                  variant="outline-light"
                  className="w-100 rounded-pill mb-2"
                  onClick={showScoreHistory}
                >
                  <FaHistory className="me-2" /> Score History
                </Button>
              </div>
            </div>

            {/* --- RIGHT COLUMN (White Info) --- */}
            <div className="col-lg-8 col-md-7 bg-white p-4 p-md-5 d-flex flex-column">
              {/* Header Desktop */}
              <div className="d-none d-md-flex justify-content-between align-items-center mb-4">
                <h5 className="fw-bold text-muted text-uppercase m-0">
                  Account Details
                </h5>
                <Button
                  variant="ghost"
                  className="text-muted"
                  onClick={() => navigate(-1)}
                >
                  <ArrowLeft size={18} className="me-2" /> Back
                </Button>
              </div>

              {/* Info Grid */}
              <div className="row g-3 mb-4">
                {userInfo.map((item, idx) => (
                  <div key={idx} className="col-sm-6">
                    <div className="info-item p-3 rounded-3 d-flex align-items-center">
                      <div className="icon-box me-3 text-primary">
                        {item.icon}
                      </div>
                      <div>
                        <small
                          className="d-block text-muted text-uppercase fw-bold"
                          style={{ fontSize: "0.7rem" }}
                        >
                          {item.label}
                        </small>
                        <span className="fw-bold text-dark">
                          {item.value || "N/A"}
                        </span>
                      </div>
                    </div>
                  </div>
                ))}
              </div>

              {/* Status Banner */}
              <div
                className={`alert ${userP?.status === "ACTIVE" ? "alert-success" : "alert-danger"} d-flex align-items-center rounded-3 border-0 mb-4`}
              >
                {userP?.status === "ACTIVE" ? (
                  <CheckCircle className="me-2" />
                ) : (
                  <Ban className="me-2" />
                )}
                <div>
                  <strong>Status: {userP?.status}</strong>
                  <div className="small">
                    This user currently has{" "}
                    {userP?.status === "ACTIVE" ? "full access" : "no access"}{" "}
                    to the system{" "}
                    {userP?.status === "ACTIVE" &&
                      `as a ` + userP?.role?.toLowerCase()}{" "}
                    .
                  </div>
                </div>
              </div>

              {/* Mobile Only: Score Button */}
              <div className="d-md-none mb-4">
                <Button
                  variant="primary"
                  className="w-100 rounded-pill py-2 shadow-sm"
                  onClick={showScoreHistory}
                >
                  <FaHistory className="me-2" /> View Score History
                </Button>
              </div>
              {/* isOnline */}


                <div className={`d-flex align-items-center gap-2 mb-4 ${isOnline ? "text-success" : "text-danger"}`}>
                  {isOnline ? (
                    <><CheckCircle size={18} /> Online</>
                  ) : (
                    <><Ban size={18} /> Offline</>
                  )}
                </div>


              {/* --- ACTION ZONE (The Dangerous Buttons) --- */}
              {user?.role === userRoles.admin && (
                <div className="mt-auto border-top pt-4">
                  <h6 className="fw-bold text-danger mb-3 d-flex align-items-center">
                    <FaUserShield className="me-2" /> Admin Actions
                  </h6>
                  <div className="d-flex flex-wrap gap-2">
                    {/* Role Management */}
                    {userP?.role === userRoles.student && (
                      <Button
                        variant="outline-primary"
                        className="rounded-pill action-btn flex-grow-1"
                        onClick={() => confirmActionTrigger("make-supervisor")}
                      >
                        <ShieldCheck size={18} className="me-2" /> Promote to
                        Supervisor
                      </Button>
                    )}

                    {userP?.role === userRoles.supervisor && (
                      <Button
                        variant="outline-warning"
                        className="rounded-pill action-btn flex-grow-1"
                        onClick={() =>
                          confirmActionTrigger("remove-supervisor")
                        }
                      >
                        <ShieldOff size={18} className="me-2" /> Remove
                        Supervisor
                      </Button>
                    )}

                    {/* Block / Unblock */}
                    {userP?.role !== userRoles.admin &&
                      (userP?.status === "ACTIVE" ? (
                        <Button
                          variant="danger"
                          className="rounded-pill action-btn flex-grow-1"
                          onClick={() => confirmActionTrigger("block-user")}
                        >
                          <Ban size={18} className="me-2" /> Block Access
                        </Button>
                      ) : (
                        <Button
                          variant="success"
                          className="rounded-pill action-btn flex-grow-1"
                          onClick={() => confirmActionTrigger("unblock-user")}
                        >
                          <CheckCircle size={18} className="me-2" /> Unblock
                          Access
                        </Button>
                      ))}
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* --- CONFIRMATION MODAL --- */}
      <Modal
        show={showModal}
        onHide={() => setShowModal(false)}
        centered
        backdrop="static"
        className="confirmation-modal"
      >
        <Modal.Body className="text-center p-4">
          <div className="mb-3">{modalContent.icon}</div>
          <h4 className="fw-bold mb-2 text-dark">{modalContent.title}</h4>
          <p className="text-muted mb-4">{modalContent.body}</p>
          <div className="d-flex justify-content-center gap-2">
            <Button
              variant="light"
              onClick={() => setShowModal(false)}
              className="px-4 rounded-pill"
              disabled={actionLoading}
            >
              Cancel
            </Button>
            <Button
              variant={modalContent.variant}
              onClick={handleConfirmAction}
              className="px-4 rounded-pill shadow-sm"
              disabled={actionLoading}
            >
              {actionLoading ? (
                <Spinner size="sm" animation="border" />
              ) : (
                modalContent.btnText
              )}
            </Button>
          </div>
        </Modal.Body>
      </Modal>

      {/* Internal Styling */}
      <style>{`
        /* Background & Layout */
        .profile-wrapper {
          min-height: 100vh;
          background-color: #eff1f5; /* Light Gray Background - Not White */
          display: flex;
          align-items: center;
        }

        .profile-card {
          min-height: 600px;
          border-radius: 24px;
        }

        /* Left Panel - The "Pro" Look */
        .left-panel {
          background: linear-gradient(135deg, #4361ee 0%, #3f37c9 100%); /* Same Primary Colors */
          position: relative;
          overflow: hidden;
        }
        
        /* Subtle Pattern Overlay */
        .left-panel::before {
           content: '';
           position: absolute;
           top: 0; left: 0; right: 0; bottom: 0;
           background: radial-gradient(circle at top right, rgba(255,255,255,0.1) 0%, transparent 60%);
           pointer-events: none;
        }

        .profile-avatar {
          width: 150px;
          height: 150px;
          border-radius: 50%;
          border: 5px solid rgba(255,255,255,0.2);
          object-fit: cover;
          box-shadow: 0 10px 20px rgba(0,0,0,0.2);
        }

        .avatar-wrapper {
          position: relative;
          display: inline-block;
        }

        .status-dot {
          width: 20px;
          height: 20px;
          border-radius: 50%;
          border: 3px solid #fff;
          position: absolute;
          bottom: 10px;
          right: 10px;
        }
        .status-dot.online { background-color: #06d6a0; }
        .status-dot.offline { background-color: #ef476f; }

        /* Right Panel Info Items */
        .info-item {
          background-color: #f8f9fa;
          border: 1px solid #e9ecef;
          transition: transform 0.2s, border-color 0.2s;
        }
        .info-item:hover {
          border-color: #4361ee;
          transform: translateY(-2px);
          background-color: #fff;
          box-shadow: 0 4px 10px rgba(0,0,0,0.05);
        }
        .icon-box {
          width: 40px;
          height: 40px;
          border-radius: 10px;
          background-color: rgba(67, 97, 238, 0.1);
          display: flex;
          align-items: center;
          justify-content: center;
          font-size: 1.2rem;
        }

        /* Modal Tweaks */
        .confirmation-modal .modal-content {
           border-radius: 20px;
           border: none;
           box-shadow: 0 10px 30px rgba(0,0,0,0.1);
        }

        /* Mobile Tweaks */
        @media (max-width: 768px) {
          .profile-card {
            min-height: auto;
            border-radius: 0; /* Full width on mobile looks cleaner without round corners sometimes, or keep 15px */
            margin-top: 60px; /* Space for Navbar if exists */
            border-radius: 20px;
          }
          .left-panel {
            padding-bottom: 3rem !important;
            border-radius: 20px 20px 0 0;
          }
          .action-btn {
             width: 100%;
             margin-bottom: 0.5rem;
          }
        }
      `}</style>
    </div>
  );
}
